import React, { useState, useMemo } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { sanitizeImageUrl, sanitizeText } from '../../../shared/utils/urlSanitizer';

const GenericCarousel = ({ images }) => {
    const [index, setIndex] = useState(0);
    const { t } = useTranslation();

    // Sanitize and validate images array to prevent XSS
    const sanitizedImages = useMemo(() => {
        if (!images || !Array.isArray(images)) {
            return [];
        }

        return images.filter(image => {
            // Ensure image is an object with required properties
            return image && 
                   typeof image === 'object' && 
                   image.src && 
                   typeof image.src === 'string' &&
                   image.src.trim().length > 0;
        }).map((image, index) => {
            // Create a sanitized version of each image object
            const sanitizedSrc = sanitizeImageUrl(image.src);
            
            // If sanitization fails, skip this image
            if (!sanitizedSrc) {
                return null;
            }

            return {
                src: sanitizedSrc,
                alt: sanitizeText(
                    image.alt ?? 
                    `${t('Imagem')}${index > 0 ? ` (${index + 1})` : ''}`
                ),
                // Ensure no other properties can introduce XSS
                ...(image.title && { title: sanitizeText(image.title) })
            };
        }).filter(Boolean); // Remove null entries
    }, [images, t]);

    const handleSelect = (selectedIndex) => {
        // Validate the selected index to prevent manipulation
        if (typeof selectedIndex !== 'number' || 
            selectedIndex < 0 || 
            selectedIndex >= sanitizedImages.length ||
            selectedIndex === index) {
            return;
        }

        setIndex(selectedIndex);
    };

    // If no valid images, return null to prevent rendering empty carousel
    if (!sanitizedImages || sanitizedImages.length === 0) {
        return null;
    }

    return (
        <>
            <Carousel
                activeIndex={index}
                className={`genericCarousel`}
                onSelect={handleSelect}
                prevIcon={
                    <FontAwesomeIcon icon="chevron-left" className="cortxt" />
                }
                nextIcon={
                    <FontAwesomeIcon icon="chevron-right" className="cortxt" />
                }
                indicators={false}
                interval={null}
            >
                {sanitizedImages.map((image, i) => (
                    <Carousel.Item key={`carousel-item-${i}`}>
                        <div className="img-fluid imgCarousel d-inline-block">
                            <img
                                alt={image.alt}
                                src={image.src}
                                className="img-fluid b-w1 b-solid b-cinza2 pad2 rad10"
                                loading="lazy"
                                onError={(e) => {
                                    // Prevent broken image XSS and provide fallback
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                    </Carousel.Item>
                ))}
            </Carousel>
            {/* custom indicators */}
            {sanitizedImages.length > 1 && (
                <div className={'customIndicatorsContainer mt-2'}>
                    {sanitizedImages.map((image, i) => {
                        const isActiveIndicator = i === index;
                        return (
                            <div
                                key={`indicator-${i}`}
                                className={classnames('customIndicator', {
                                    ['border-tema']: isActiveIndicator,
                                })}
                                onClick={() => handleSelect(i)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSelect(i);
                                    }
                                }}
                                aria-label={`${t('Ir para imagem')} ${i + 1}`}
                            >
                                <img
                                    alt={`${t('Miniatura da imagem')} ${i + 1}`}
                                    src={image.src}
                                    className="w-100 img-fluid b-w1 b-solid b-cinza2 pad2 rad10"
                                    loading="lazy"
                                    onError={(e) => {
                                        // Prevent broken image XSS and provide fallback
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            {/* custom indicators */}
        </>
    );
};
export default GenericCarousel;
